import React, { useMemo } from "react";

import { Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useCommitmentsSelector, useDealDefaultCurrencySelect, useDealSelector, useDebtActions } from "store";
import { AmortizationRow, CurrencyType, DebtSegment, ICommitment } from "store/types";
import { getWithdrawalSegments } from "utils/debt";

import { currencySign as currencySigns } from "../../constants";

const today = new Date().toISOString().split("T")[0];

const chartColors = ["#071F35", "#00A3B1", "#D4A24C", "#B3BBC3"];

const chartDimensions = {
  width: 300,
  height: 140
};

const legendStyle = {
  verticalAlign: "middle" as const,
  layout: "vertical" as const,
  align: "right" as const,
  maxHeight: 150,
  width: 130,
  itemStyle: {
    fontSize: "14px",
    fontWeight: "normal",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis"
  },
  symbolHeight: 10,
  symbolWidth: 10,
  symbolRadius: 5,
  itemMarginTop: 4,
  itemMarginBottom: 4,
  x: 0,
  y: 0,
  enabled: true
};

const calculateUnfundedAmount = (commitments: ICommitment[], segments: DebtSegment[]) => {
  const totalCommitments = commitments.reduce((acc, commitment) => acc + Number(commitment.amount), 0);
  const totalLoans = segments.reduce((acc, segment) => acc + Number(segment.loan_amount), 0);
  return totalCommitments - totalLoans;
};

const getChartData = (segments: DebtSegment[], payments: AmortizationRow[], unfundedAmount: number) => {
  const segmentData = payments.map((payment) => ({
    name: payment.sub_deal,
    y: payment.segmentIds.reduce((acc, id) => {
      const segment = segments.find((s) => s.id === id);
      return acc + (segment?.loan_amount || 0);
    }, 0)
  }));

  if (unfundedAmount > 0) {
    segmentData.push({
      name: "Unfunded",
      y: unfundedAmount
    });
  }

  return segmentData;
};

const OverviewCard: React.FC = () => {
  const { getAmortizationTableAction } = useDebtActions();
  const deal = useDealSelector();
  const commitments = useCommitmentsSelector();
  const currency = useDealDefaultCurrencySelect();
  const currencySign = currencySigns[currency as CurrencyType];

  const { data, isLoading } = useQuery({
    queryKey: ["amortizationTable", deal.id, today],
    queryFn: () => (deal.id ? getAmortizationTableAction(deal.id as number, today) : null)
  });

  const reportPayment = useMemo(() => {
    return data?.table.rows.find((row) => row.date === today);
  }, [data]);

  const payments = reportPayment ? reportPayment.children || [reportPayment] : [];

  const withdrawalSegments = getWithdrawalSegments(data?.segments || []);

  const options: Highcharts.Options = useMemo(() => {
    const unfundedAmount = calculateUnfundedAmount(commitments, withdrawalSegments);
    const chartData = getChartData(withdrawalSegments, payments, unfundedAmount);

    return {
      chart: {
        type: "pie",
        ...chartDimensions
      },
      exporting: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: legendStyle,
      tooltip: {
        pointFormat: `${currencySign}{point.y:,.0f}`
      },
      colors: chartColors,
      plotOptions: {
        pie: {
          dataLabels: { enabled: false },
          showInLegend: true
        }
      },
      series: [
        {
          type: "pie",
          data: chartData
        }
      ]
    };
  }, [withdrawalSegments, commitments, currencySign]);

  return (
    <Stack
      sx={{
        gap: "8px",
        borderRadius: 16,
        p: "16px",
        bgcolor: "white",
        boxShadow: "sm",
        overflow: "hidden",
        flexGrow: 1
      }}
    >
      <AsyncContentMui spinnerProps={{ size: 100 }} isLoading={isLoading}>
        <Typography sx={{ fontSize: "18px", color: "#071F35", fontWeight: 500 }}>Overview</Typography>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </AsyncContentMui>
    </Stack>
  );
};

export default OverviewCard;
